import React from 'react';
import { FileText, ClipboardList, TrendingUp, BarChart3 } from 'lucide-react';
import { Button } from '../../ui/button';
import { Badge } from '../../ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '../../ui/card';

const EtapaLevantamento = ({ pmbokAreas, questionnaireAreas, scrollToMaturity }) => {
  return (
    <section className="py-20 bg-zinc-950">
      <div className="container mx-auto px-4 lg:px-8">
        <div className="max-w-6xl mx-auto">
          {/* Header da Etapa */}
          <div className="flex items-center gap-4 mb-8">
            <div className="w-16 h-16 bg-red-600 rounded-xl flex items-center justify-center">
              <span className="text-3xl font-bold text-white">1</span>
            </div>
            <div>
              <Badge className="bg-blue-600/20 text-blue-400 border-blue-500/30 mb-2">
                Duração: 2-4 semanas
              </Badge>
              <h2 className="text-3xl font-bold text-white">Levantamento de Informações</h2>
            </div>
          </div>

          <p className="text-lg text-gray-300 mb-10 max-w-3xl">
            A primeira etapa é fundamental para entender o <strong className="text-white">contexto do negócio</strong>, 
            não apenas os aspectos técnicos. Nosso objetivo é compreender profundamente sua organização 
            para definir a melhor estratégia de investimento em BIM com retorno garantido.
          </p>

          {/* PMBOK Areas */}
          <div className="mb-12">
            <h3 className="text-2xl font-bold text-white mb-6 flex items-center gap-3">
              <FileText className="text-red-500" />
              Áreas de Conhecimento (PMBOK 6ª Edição)
            </h3>
            <p className="text-gray-400 mb-6">
              Utilizamos as áreas de conhecimento do PMBOK como base para um levantamento completo e estruturado:
            </p>
            
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
              {pmbokAreas.map((area, index) => {
                const Icon = area.icon;
                return (
                  <Card key={area.area} className="bg-zinc-900 border-zinc-800 hover:border-red-500/50 transition-all">
                    <CardHeader className="pb-2">
                      <div className="flex items-center gap-3">
                        <Icon className="text-red-500" size={20} />
                        <CardTitle className="text-white text-sm">{area.area}</CardTitle>
                      </div>
                    </CardHeader>
                    <CardContent>
                      <ul className="space-y-1">
                        {area.items.map((item, i) => (
                          <li key={`${area.area}-${item}-${i}`} className="text-xs text-gray-400 flex items-start gap-2">
                            <span className="text-red-500 mt-1">•</span>
                            {item}
                          </li>
                        ))}
                      </ul>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          </div>

          {/* Questionário Visual */}
          <div className="mb-12">
            <h3 className="text-2xl font-bold text-white mb-6 flex items-center gap-3">
              <ClipboardList className="text-red-500" />
              Questionário de Diagnóstico Organizacional
            </h3>
            <p className="text-gray-400 mb-6">
              Nosso questionário abrange diversas áreas para entender o <strong className="text-white">"business"</strong> 
              e não apenas definir software e treinamento:
            </p>

            <div className="bg-zinc-900 border border-zinc-800 rounded-2xl p-8">
              <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
                {questionnaireAreas.map((q, index) => (
                  <div key={q.area} className="text-center">
                    <div className="w-16 h-16 mx-auto bg-gradient-to-br from-red-600 to-red-800 rounded-full flex items-center justify-center mb-3">
                      <ClipboardList className="text-white" size={28} />
                    </div>
                    <p className="text-white font-medium text-sm">{q.area}</p>
                  </div>
                ))}
              </div>
              
              <div className="border-t border-zinc-800 pt-6">
                <div className="flex items-center justify-center gap-4 text-center">
                  <div>
                    <span className="text-4xl font-bold text-red-500">+250</span>
                    <p className="text-gray-400 text-sm">Perguntas Estratégicas</p>
                  </div>
                  <div className="h-12 w-px bg-zinc-700"></div>
                  <div>
                    <span className="text-4xl font-bold text-red-500">+8</span>
                    <p className="text-gray-400 text-sm">Áreas Analisadas</p>
                  </div>
                  <div className="h-12 w-px bg-zinc-700"></div>
                  <div>
                    <span className="text-4xl font-bold text-red-500">360°</span>
                    <p className="text-gray-400 text-sm">Visão do Negócio</p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* ROI Section */}
          <div className="mb-12">
            <h3 className="text-2xl font-bold text-white mb-6 flex items-center gap-3">
              <TrendingUp className="text-red-500" />
              Definição do Melhor Investimento e ROI
            </h3>
            
            <div className="grid md:grid-cols-3 gap-6">
              <Card className="bg-gradient-to-br from-green-900/30 to-zinc-900 border-green-500/30">
                <CardHeader>
                  <CardTitle className="text-green-400 text-lg">Análise de Investimento</CardTitle>
                </CardHeader>
                <CardContent className="text-gray-300 text-sm">
                  <ul className="space-y-2">
                    <li>• Mapeamento de custos atuais</li>
                    <li>• Identificação de desperdícios</li>
                    <li>• Priorização de investimentos</li>
                    <li>• Cronograma financeiro</li>
                  </ul>
                </CardContent>
              </Card>
              
              <Card className="bg-gradient-to-br from-blue-900/30 to-zinc-900 border-blue-500/30">
                <CardHeader>
                  <CardTitle className="text-blue-400 text-lg">Projeção de Retorno</CardTitle>
                </CardHeader>
                <CardContent className="text-gray-300 text-sm">
                  <ul className="space-y-2">
                    <li>• Redução de retrabalho</li>
                    <li>• Ganho de produtividade</li>
                    <li>• Melhoria na qualidade</li>
                    <li>• Vantagem competitiva</li>
                  </ul>
                </CardContent>
              </Card>
              
              <Card className="bg-gradient-to-br from-purple-900/30 to-zinc-900 border-purple-500/30">
                <CardHeader>
                  <CardTitle className="text-purple-400 text-lg">Métricas de Sucesso</CardTitle>
                </CardHeader>
                <CardContent className="text-gray-300 text-sm">
                  <ul className="space-y-2">
                    <li>• KPIs de implementação</li>
                    <li>• Marcos de maturidade</li>
                    <li>• Indicadores de ROI</li>
                    <li>• Benchmarks do setor</li>
                  </ul>
                </CardContent>
              </Card>
            </div>
          </div>

          {/* BIM Maturity Assessment CTA */}
          <div className="bg-gradient-to-r from-red-900/30 to-zinc-900 border border-red-500/30 rounded-2xl p-8 text-center">
            <BarChart3 className="mx-auto text-red-500 mb-4" size={48} />
            <h3 className="text-2xl font-bold text-white mb-4">
              Avaliação de Maturidade BIM
            </h3>
            <p className="text-gray-300 mb-6 max-w-2xl mx-auto">
              Baseado na metodologia de <strong className="text-white">Bilal Succar</strong>, 
              descubra em que nível de maturidade BIM sua empresa está. 
              Preencha a matriz e receba um relatório completo com gráficos e recomendações.
            </p>
            <Button 
              size="lg" 
              className="bg-red-600 hover:bg-red-700 text-white"
              onClick={scrollToMaturity}
            >
              <BarChart3 className="mr-2" size={20} />
              Analisar Nível de Maturidade BIM
            </Button>
          </div>
        </div>
      </div>
    </section>
  );
};

export default EtapaLevantamento;
